import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';
import * as api from '../api';
import Dropdown from '../components/Dropdown';
import './Cabinet.css';

// ── Shared helpers ──────────────────────────────────────────────────────────

// Every tab is "fetch once, show, maybe reload". This keeps that in one place
// instead of five copies of the same loading/error/state triple.
function useAsync(loader, deps) {
  const [state, setState] = useState({ data: null, error: '', loading: true });
  const reload = useCallback(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    loader()
      .then((data) => alive && setState({ data, error: '', loading: false }))
      .catch((e) => alive && setState({ data: null, error: e.message || 'Error', loading: false }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => reload(), [reload]);
  return { ...state, reload };
}

function Panel({ state, children, empty }) {
  const { t } = useLang();
  if (state.loading) return <p className="cab-muted">{t('cab_loading')}</p>;
  if (state.error) return <p className="cab-error">{state.error}</p>;
  if (empty) return <p className="cab-muted">{t('cab_none')}</p>;
  return children;
}

// Weekday names come from the browser rather than the translation file: the
// site already knows the language, and Intl spells them correctly in all three.
const weekdayName = (lang, index) => {
  // 2024-01-07 was a Sunday, so adding the index lands on the right day.
  const date = new Date(Date.UTC(2024, 0, 7 + index));
  return new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' }).format(date);
};

const asDate = (value) => (value ? String(value).slice(0, 10) : '—');
// A lesson is a date and, when a student sits more than one that day, a time.
// Most records carry no time and read as a plain date, exactly as before.
const lessonMoment = (record) =>
  `${asDate(record.lesson_date)}${record.lesson_time ? ` · ${record.lesson_time}` : ''}`;
const money = (amount, currency) =>
  `${Number(amount).toLocaleString('ru-RU')} ${currency || 'UZS'}`;

// A password field the office can read while typing it, generate a decent one
// into, and copy out. Passwords are stored one-way, so the only moment anyone
// can see one is here — which is exactly why this exists.
function PasswordField({ label, value, onChange }) {
  const { t } = useLang();
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);

  // Ambiguous characters are left out: these get read aloud and written down.
  const generate = () => {
    const alphabet = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const bytes = new Uint32Array(12);
    crypto.getRandomValues(bytes);
    onChange(Array.from(bytes, (n) => alphabet[n % alphabet.length]).join(''));
    setShown(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is on screen either way.
      setShown(true);
    }
  };

  return (
    <>
      <label className="cab-wide">
        {label}
        <input type={shown ? 'text' : 'password'} minLength={8} autoComplete="new-password"
               value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
      <div className="cab-btn-row">
        <button type="button" className="cab-btn-ghost" onClick={() => setShown(!shown)}>
          {shown ? t('cab_hide') : t('cab_show')}
        </button>
        <button type="button" className="cab-btn-ghost" onClick={generate}>{t('cab_generate')}</button>
        <button type="button" className="cab-btn-ghost" disabled={!value} onClick={copy}>
          {copied ? t('cab_copied') : t('cab_copy')}
        </button>
      </div>
    </>
  );
}

// Decoration for the header, on the theme of what the academy actually sells:
// a certificate with a sealed ribbon and a graduate's cap. Deliberately
// wordless — naming IELTS or CSCA here would date the moment the course list
// changes, and this has to keep working for years without anyone editing it.
//
// Purely ornamental: it sits behind the content, ignores the pointer, is
// hidden from screen readers, and disappears on narrow screens where there is
// no room to spare.
function HeaderArt() {
  return (
    <svg className="cab-art" viewBox="0 0 280 180" aria-hidden="true" focusable="false">
      <circle cx="214" cy="58" r="62" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="128" cy="128" r="34" fill="var(--orange)" opacity="0.16" />

      {/* The certificate, tilted as if lying on a desk */}
      <g transform="rotate(-7 150 90)">
        <rect x="96" y="34" width="130" height="94" rx="8"
              fill="var(--white)" opacity="0.14" stroke="var(--white)" strokeOpacity="0.35" strokeWidth="2" />
        <g stroke="var(--white)" strokeOpacity="0.4" strokeWidth="3" strokeLinecap="round">
          <path d="M112 58 H186" />
          <path d="M112 72 H210" />
          <path d="M112 86 H196" />
          <path d="M112 100 H170" />
        </g>
        {/* Seal and ribbon */}
        <circle cx="200" cy="106" r="15" fill="var(--orange)" opacity="0.85" />
        <circle cx="200" cy="106" r="8" fill="none" stroke="var(--white)" strokeOpacity="0.6" strokeWidth="2" />
        <path d="M193 118 L190 136 L200 130 L210 136 L207 118 Z"
              fill="var(--orange-light)" opacity="0.8" />
      </g>

      {/* Graduate's cap */}
      <g transform="translate(24 96)" opacity="0.7">
        <path d="M0 14 L28 2 L56 14 L28 26 Z" fill="var(--white)" fillOpacity="0.35" />
        <path d="M12 20 V34 C12 40 44 40 44 34 V20" fill="none"
              stroke="var(--white)" strokeOpacity="0.35" strokeWidth="3" strokeLinecap="round" />
        <path d="M56 14 V32" stroke="var(--orange-light)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="56" cy="35" r="4" fill="var(--orange-light)" />
      </g>
    </svg>
  );
}

// The one component that says "this student owes money". Red, unmissable, and
// blunt about the consequence, because a quiet grey line about a balance is
// how somebody arrives at a lesson they are not being let into.
function DebtBanner({ debt }) {
  const { t } = useLang();
  if (!debt || !debt.has_debt) return null;
  return (
    <div className={debt.blocked ? 'cab-debt cab-debt-blocked' : 'cab-debt'}>
      <strong>{t('cab_debt_title')}</strong>{' '}
      {t('cab_debt_lessons', { n: debt.unpaid_lessons })}
      {debt.blocked && <div className="cab-debt-block">{t('cab_debt_blocked')}</div>}
      {!debt.blocked && (
        <div className="cab-debt-soon">
          {t('cab_debt_warn', { left: debt.unpaid_limit - debt.unpaid_lessons })}
        </div>
      )}
    </div>
  );
}

// The same thing shrunk to fit inside a table cell or a list of children.
function DebtTag({ debt }) {
  const { t } = useLang();
  if (!debt) return null;
  if (!debt.has_debt) return <span className="cab-ok">{t('cab_debt_none')}</span>;
  return (
    <span className={debt.blocked ? 'cab-warn cab-warn-strong' : 'cab-warn'}>
      {t('cab_debt_lessons', { n: debt.unpaid_lessons })}
      {debt.blocked ? ` — ${t('cab_debt_not_admitted')}` : ''}
    </span>
  );
}

// «Нажала «забыли пароль», письма нет» — вопрос к офису, а ответ на него до
// сих пор лежал только в логе сервера в панели Render, куда офису ходить
// незачем и обычно нечем. Поэтому он здесь, над журналом действий: сначала
// то, как почта настроена, потом чем кончились последние попытки.
//
// Исходы живут в памяти сервера и пропадают при перезапуске. Это не журнал,
// а ответ на «я только что нажала» — на такой срок памяти и хватает.
function MailStatus({ state }) {
  const { t } = useLang();
  const d = state.data;
  if (state.loading || state.error || !d) return null;

  const outcomes = {
    sent: t('cab_mail_sent'),
    failed: t('cab_mail_failed'),
    'no-account': t('cab_mail_no_account'),
    'mail-off': t('cab_mail_off_short'),
  };

  return (
    <div className="cab-mail">
      <h3>{t('cab_mail_title')}</h3>
      {!d.enabled && (
        <p className="cab-muted">
          {t('cab_mail_off')}
          {d.missing.length > 0 && <> {d.missing.join(', ')}</>}
        </p>
      )}
      {d.enabled && (
        <p className="cab-muted">
          {t('cab_mail_on')} <b>{d.from}</b>, {t('cab_mail_links')} <b>{d.siteUrl}</b>
        </p>
      )}
      {d.testSender && <p className="cab-error">{t('cab_mail_test_sender')}</p>}

      {d.attempts.length === 0 ? (
        <p className="cab-muted">{t('cab_mail_no_attempts')}</p>
      ) : (
        <table className="cab-table">
          <thead><tr><th>{t('cab_date')}</th><th>{t('login_email')}</th><th>{t('cab_status')}</th></tr></thead>
          <tbody>
            {d.attempts.map((a, i) => (
              <tr key={`${a.at}-${i}`}>
                <td data-label={t('cab_date')}>{new Date(a.at).toLocaleString()}</td>
                <td data-label={t('login_email')}>{a.email || '—'}</td>
                <td data-label={t('cab_status')}>
                  {outcomes[a.outcome] || a.outcome}
                  {a.detail && <> <span className="cab-muted">{a.detail}</span></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Пустое поле формы значит «не оговорено», и на сервер должно уехать null,
// а не пустая строка и не ноль: ноль — это обещанная цена в ноль сумов.
// Срок — исключение: он не может быть неизвестным, и пустое поле означает
// «как обычно», то есть значение по умолчанию на сервере.
function contractTerms(form) {
  const orNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const body = {
    contract_start: form.contract_start,
    price_per_month: orNull(form.price_per_month),
    discount_percent: orNull(form.discount_percent) ?? 0,
    lessons_per_week: orNull(form.lessons_per_week),
    lessons_per_month: orNull(form.lessons_per_month),
    note: form.note || null,
  };
  if (form.term_months !== '' && form.term_months !== null) body.term_months = Number(form.term_months);
  return body;
}

// Поля договора. Одни и те же и при заведении, и при правке — иначе рано или
// поздно в одной из двух форм окажется на поле меньше.
function ContractFields({ value, onChange }) {
  const { t } = useLang();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="cab-contract-fields">
      <label>{t('cab_start')}
        <input type="date" value={value.contract_start} onChange={set('contract_start')} /></label>
      <label>{t('cab_contract_term_months')}
        <input type="number" min="1" max="60" placeholder="12"
               value={value.term_months} onChange={set('term_months')} /></label>
      <label>{t('cab_contract_price')}
        <input type="number" min="0" step="1000" value={value.price_per_month}
               onChange={set('price_per_month')} /></label>
      <label>{t('cab_contract_discount')}
        <input type="number" min="0" max="100" value={value.discount_percent}
               onChange={set('discount_percent')} /></label>
      <label>{t('cab_contract_per_week')}
        <input type="number" min="1" max="7" value={value.lessons_per_week}
               onChange={set('lessons_per_week')} /></label>
      <label>{t('cab_contract_per_month')}
        <input type="number" min="1" max="62" value={value.lessons_per_month}
               onChange={set('lessons_per_month')} /></label>
      <label className="cab-contract-note">{t('cab_contract_note')}
        <input type="text" maxLength={500} value={value.note} onChange={set('note')} /></label>
    </div>
  );
}

// «3 раз в неделю» — то, что выдаёт подстановка числа в одну форму слова.
// Русский требует трёх, и правило то же, что уже применяется к годам опыта:
// один урок, два урока, пять уроков, и исключение на -надцать.
function plural(t, n, base) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return t(`${base}_one`);
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return t(`${base}_few`);
  return t(`${base}_many`);
}

// Условия одной строкой под договором. Поля, о которых не договаривались,
// не показываются вовсе: заголовок над пустотой читается как поломка.
function ContractTerms({ c }) {
  const { t } = useLang();
  const bits = [];
  if (c.lessons_per_week) bits.push(`${c.lessons_per_week} ${plural(t, c.lessons_per_week, 'cab_week')}`);
  if (c.lessons_per_month) bits.push(`${c.lessons_per_month} ${plural(t, c.lessons_per_month, 'cab_lesson')}`);
  if (c.monthly_due !== null && c.monthly_due !== undefined) {
    const discount = Number(c.discount_percent ?? 0);
    bits.push(`${t('cab_contract_due')}: ${money(c.monthly_due, c.currency)}` +
              (discount > 0 ? ` (−${discount}%)` : ''));
  }
  if (c.note) bits.push(c.note);
  if (!bits.length) return <p className="cab-muted cab-contract-terms">{t('cab_contract_not_set')}</p>;
  return <p className="cab-muted cab-contract-terms">{bits.join(' · ')}</p>;
}

function Stat({ label, value, hint }) {
  return (
    <div className="cab-stat">
      <span className="cab-stat-label">{label}</span>
      <strong className="cab-stat-value">{value}</strong>
      {hint && <span className="cab-stat-hint">{hint}</span>}
    </div>
  );
}

// ── The manual (every role has its own) ─────────────────────────────────────
// The text comes from the server already narrowed to the reader's role, and
// arrives as structure rather than markup — so it renders with the cabinet's
// own styles and nothing is injected as HTML.
//
// Not a page on the public site on purpose: that ships as one bundle, so an
// "unlisted" help page would reach every visitor whether or not they opened it.
function HelpTab() {
  const help = useAsync(() => api.getHelp(), []);
  const d = help.data;

  const renderBlock = (b, i) => {
    if (b.type === 'text') return <p key={i}>{b.text}</p>;
    if (b.type === 'callout') {
      return <p key={i} className={b.tone === 'warn' ? 'cab-error' : 'cab-note'}>{b.text}</p>;
    }
    if (b.type === 'list' || b.type === 'steps') {
      const Tag = b.type === 'steps' ? 'ol' : 'ul';
      return (
        <Tag key={i} className={b.type === 'steps' ? 'cab-steps' : 'cab-list'}>
          {b.items.map((item, n) => (
            <li key={n}>
              {item.strong && <strong>{item.strong} </strong>}
              {item.text}
            </li>
          ))}
        </Tag>
      );
    }
    if (b.type === 'pairs') {
      return (
        <dl className="cab-pairs" key={i}>
          {b.items.map((item, n) => (
            <React.Fragment key={n}>
              <dt>{item.term}</dt>
              <dd>{item.text}</dd>
            </React.Fragment>
          ))}
        </dl>
      );
    }
    return null;
  };

  return (
    <Panel state={help} empty={!d}>
      {d && (
        <>
          <div className="cab-card">
            <h3>{d.title}</h3>
            <p className="cab-muted">{d.intro}</p>
          </div>
          {d.sections.map((section, i) => (
            <div className="cab-card" key={i}>
              <h3>{section.heading}</h3>
              {section.note && <p className="cab-muted">{section.note}</p>}
              {section.blocks.map(renderBlock)}
            </div>
          ))}
        </>
      )}
    </Panel>
  );
}

// ── Password (every role has it) ────────────────────────────────────────────
function PasswordTab() {
  const { t } = useLang();
  const { signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.changePassword(current, next);
      // The server drops every session on a password change, so the only
      // honest thing to do is send the person back to the sign-in screen.
      await signOut();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="cab-form cab-form-narrow" onSubmit={submit}>
      {error && <p className="cab-error">{error}</p>}
      <label>
        {t('cab_current_password')}
        <input type="password" autoComplete="current-password" required
               value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>
        {t('cab_new_password')}
        <input type="password" autoComplete="new-password" minLength={8} required
               value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <button type="submit" disabled={busy}>{t('cab_save')}</button>
      <p className="cab-muted">{t('cab_password_changed')}</p>
    </form>
  );
}

// ── Student and parent ──────────────────────────────────────────────────────
function LearnerCabinet({ tab, childId }) {
  const { t, lang } = useLang();
  const [answers, setAnswers] = useState({});

  const overview = useAsync(() => api.getOverview(childId), [childId]);
  const schedule = useAsync(() => api.getSchedule(childId), [childId]);
  const homework = useAsync(() => api.getHomework(childId), [childId]);
  const attendance = useAsync(() => api.getAttendance(childId), [childId]);
  const payments = useAsync(() => api.getPayments(childId), [childId]);
  const course = useAsync(() => api.getMyCourse(childId), [childId]);
  const { user } = useAuth();
  const readOnly = user.role === 'parent';

  if (tab === 'overview') {
    const d = overview.data;
    return (
      <Panel state={overview} empty={!d}>
        {d && (
          <>
            <DebtBanner debt={d.debt} />
            <div className="cab-stats">
              <Stat label={t('cab_lessons_left')} value={d.package.lessons_left}
                    hint={`${t('cab_lessons_paid')}: ${d.package.lessons_paid}`} />
              <Stat label={t('cab_lessons_used')} value={d.package.lessons_used}
                    hint={t('cab_package_note')} />
              <Stat label={t('cab_present')} value={d.attendance.present} />
              <Stat label={t('cab_missed')} value={d.attendance.missed} />
            </div>
            <div className="cab-card">
              <h3>{t('cab_contract')}</h3>
              {d.contract ? (
                <p>
                  {asDate(d.contract.contract_start)} — {asDate(d.contract.contract_end)}
                  <span className="cab-tag">{t('cab_contract_term')}</span>
                </p>
              ) : <p className="cab-muted">{t('cab_none')}</p>}
              <p className="cab-muted">{t('cab_package_note')}</p>
            </div>
            {/* Which group, and who teaches it. The schedule only ever said
                "group" as a format, which told the person attending nothing. */}
            {d.groups.length > 0 && (
              <div className="cab-card">
                <h3>{t('cab_tab_groups')}</h3>
                <ul className="cab-list">
                  {d.groups.map((g) => (
                    <li key={g.id}>
                      {g.name}
                      {g.teacher_name && <span className="cab-muted"> · {g.teacher_name}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {d.homework_due.length > 0 && (
              <div className="cab-card">
                <h3>{t('cab_tab_homework')}</h3>
                <ul className="cab-list">
                  {d.homework_due.map((h) => (
                    <li key={h.id}>{h.title} <span className="cab-muted">{t('cab_due')}: {asDate(h.due_date)}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </Panel>
    );
  }

  if (tab === 'course') {
    const d = course.data;
    const next = d?.next;
    const past = d?.lessons || [];
    // What the student's own mark on a lesson says, in words rather than a
    // status code: "present" is a fact about the past, and a lesson with no
    // mark yet has not happened or has not been taken yet.
    const attendanceLabel = (lesson) => {
      if (lesson.attendance_status === 'present') return t('cab_you_were_here');
      if (lesson.attendance_status === 'missed') return t('cab_you_missed');
      return t('cab_not_marked');
    };
    return (
      <Panel state={course} empty={!next && !past.length}>
        {d && (
          <>
            {/* One lesson, large, at the top: the thing a student opens the
                cabinet to find out is what is next and what it is about. */}
            {next && (
              <div className="cab-card cab-next-lesson">
                <span className="cab-tag">{t('cab_next_lesson')}</span>
                <h3>{next.topic}</h3>
                <p className="cab-muted">
                  {lessonMoment(next)}
                  {next.group_name && <> · {next.group_name}</>}
                  {next.teacher_name && <> · {next.teacher_name}</>}
                </p>
                {next.summary && <p>{next.summary}</p>}
                {next.attendance_status && (
                  <p className="cab-muted">{attendanceLabel(next)}</p>
                )}
              </div>
            )}

            {d.groups?.length > 0 && (
              <div className="cab-card">
                <h3>{t('cab_tab_groups')}</h3>
                <ul className="cab-list">
                  {d.groups.map((g) => (
                    <li key={g.id}>
                      {g.name}
                      {g.teacher_name && <span className="cab-muted"> · {g.teacher_name}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Everything already covered, newest first — the answer to "what
                did we do last week", which until now lived only in a notebook. */}
            {past.length > 0 && (
              <div className="cab-card">
                <h3>{t('cab_lessons_past')}</h3>
                <ul className="cab-lesson-list">
                  {past.map((l) => (
                    <li key={l.id}>
                      <div className="cab-lesson-line">
                        <strong>{l.topic}</strong>
                        <span className="cab-muted">{lessonMoment(l)}</span>
                      </div>
                      {l.summary && <p className="cab-muted">{l.summary}</p>}
                      <span className={`cab-pill ${l.attendance_status === 'missed' ? 'cab-pill-warn' : ''}`}>
                        {attendanceLabel(l)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="cab-note">{t('cab_lessons_note')}</p>
          </>
        )}
      </Panel>
    );
  }

  if (tab === 'schedule') {
    const slots = schedule.data?.slots || [];
    return (
      <Panel state={schedule} empty={!slots.length}>
        <table className="cab-table">
          <thead><tr><th>{t('cab_date')}</th><th>{t('cab_status')}</th><th>{t('cab_name')}</th></tr></thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.id}>
                <td data-label={t('cab_date')}>{weekdayName(lang, s.weekday)}, {s.time}</td>
                <td data-label={t('cab_status')}>{s.format}</td>
                <td data-label={t('cab_name')}>{s.teacher_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cab-note">{t('cab_no_reschedule')}</p>
      </Panel>
    );
  }

  if (tab === 'homework') {
    const items = homework.data || [];
    const send = async (id) => {
      await api.submitHomework(id, answers[id] || '');
      setAnswers((a) => ({ ...a, [id]: '' }));
      homework.reload();
    };
    return (
      <Panel state={homework} empty={!items.length}>
        {items.map((h) => (
          <div className="cab-card" key={h.id}>
            <h3>{h.title}</h3>
            <p className="cab-muted">
              {t('cab_due')}: {asDate(h.due_date)} · {h.teacher_name || '—'}
            </p>
            {h.body && <p>{h.body}</p>}
            {h.submitted_at ? (
              <div className="cab-sub">
                <p><strong>{t('cab_submitted')}:</strong> {asDate(h.submitted_at)}</p>
                <p className="cab-answer">{h.submission_text}</p>
                {h.grade && <p><strong>{t('cab_grade')}:</strong> {h.grade}</p>}
                {h.teacher_comment && <p><strong>{t('cab_teacher_comment')}:</strong> {h.teacher_comment}</p>}
              </div>
            ) : readOnly ? (
              <p className="cab-muted">{t('cab_parent_readonly')}</p>
            ) : (
              <div className="cab-sub">
                <label>
                  {t('cab_your_answer')}
                  <textarea rows={4} value={answers[h.id] || ''}
                            onChange={(e) => setAnswers((a) => ({ ...a, [h.id]: e.target.value }))} />
                </label>
                <button type="button" onClick={() => send(h.id)}>{t('cab_submit_answer')}</button>
              </div>
            )}
          </div>
        ))}
      </Panel>
    );
  }

  if (tab === 'help') return <HelpTab />;
  if (tab === 'journal') return <Journal studentId={childId || undefined} canWrite={false} />;

  if (tab === 'attendance') {
    const records = attendance.data?.records || [];
    return (
      <Panel state={attendance} empty={!records.length}>
        <table className="cab-table">
          <thead><tr><th>{t('cab_date')}</th><th>{t('cab_lesson_course')}</th><th>{t('cab_status')}</th><th>{t('cab_name')}</th></tr></thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id}>
                <td data-label={t('cab_date')}>{lessonMoment(r)}</td>
                <td data-label={t('cab_lesson_course')}>{r.group_name || '—'}</td>
                <td className={r.status === 'present' ? 'cab-ok' : 'cab-warn'} data-label={t('cab_status')}>
                  {r.status === 'present' ? t('cab_present') : t('cab_missed')}
                </td>
                <td data-label={t('cab_name')}>{r.teacher_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cab-note">{t('cab_no_reschedule')}</p>
      </Panel>
    );
  }

  if (tab === 'payments') {
    const rows = payments.data?.payments || [];
    return (
      <Panel state={payments} empty={!rows.length}>
        <DebtBanner debt={payments.data?.debt} />
        {payments.data && (
          <div className="cab-stats">
            <Stat label={t('cab_lessons_paid')} value={payments.data.package.lessons_paid} />
            <Stat label={t('cab_lessons_left')} value={payments.data.package.lessons_left} />
          </div>
        )}
        <table className="cab-table">
          <thead><tr><th>{t('cab_date')}</th><th>{t('cab_amount')}</th><th>{t('cab_name')}</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td data-label={t('cab_date')}>{asDate(p.paid_at)}</td>
                <td data-label={t('cab_amount')}>{money(p.amount, p.currency)}</td>
                <td data-label={t('cab_name')}>{p.note || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    );
  }

  return <PasswordTab />;
}

// ── Teacher ─────────────────────────────────────────────────────────────────
function TeacherCabinet({ tab }) {
  const { t, lang } = useLang();
  const schedule = useAsync(() => api.getTeacherSchedule(), []);
  const groups = useAsync(() => api.getTeacherGroups(), []);
  const students = useAsync(() => api.getTeacherStudents(), []);
  const homework = useAsync(() => api.getTeacherHomework(), []);
  const finance = useAsync(() => api.getTeacherFinance(), []);

  const [form, setForm] = useState({ title: '', body: '', due_date: '', group_id: '', student_id: '' });
  const [mark, setMark] = useState({ student_id: '', lesson_date: '', lesson_time: '', group_id: '', comment: '' });
  const [error, setError] = useState('');

  // Lessons are read one group at a time: a teacher works inside the group in
  // front of them, and the whole list across groups is a report nobody asked
  // for. The group chosen here is what every lesson action below applies to.
  const [lessonGroup, setLessonGroup] = useState('');
  const [lesson, setLesson] = useState({ lesson_date: '', lesson_time: '', topic: '', summary: '' });
  const [editingLesson, setEditingLesson] = useState(null);
  const lessons = useAsync(
    () => (lessonGroup ? api.getTeacherLessons(lessonGroup) : Promise.resolve([])),
    [lessonGroup]
  );

  const saveLesson = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingLesson) {
        await api.updateLesson(editingLesson, { topic: lesson.topic, summary: lesson.summary });
      } else {
        await api.saveLesson({
          group_id: Number(lessonGroup),
          lesson_date: lesson.lesson_date,
          lesson_time: lesson.lesson_time,
          topic: lesson.topic,
          summary: lesson.summary,
        });
      }
      setLesson({ lesson_date: '', lesson_time: '', topic: '', summary: '' });
      setEditingLesson(null);
      lessons.reload();
    } catch (err) { setError(err.message); }
  };

  const removeLesson = async (id) => {
    setError('');
    try {
      await api.deleteLesson(id);
      lessons.reload();
    } catch (err) { setError(err.message); }
  };

  // Marking from the lesson sends the lesson, not a date: the row already
  // knows its group, date and time, and the server reads them from there.
  const markAtLesson = async (lessonId, studentId, status) => {
    setError('');
    try {
      await api.markAttendance({ lesson_id: lessonId, student_id: studentId, status });
      lessons.reload();
      students.reload();
    } catch (err) { setError(err.message); }
  };

  const createHw = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.createHomework({
        title: form.title,
        body: form.body,
        due_date: form.due_date || null,
        group_id: form.group_id ? Number(form.group_id) : null,
        student_id: form.student_id ? Number(form.student_id) : null,
      });
      setForm({ title: '', body: '', due_date: '', group_id: '', student_id: '' });
      homework.reload();
    } catch (err) { setError(err.message); }
  };

  const sendMark = async (status) => {
    setError('');
    try {
      await api.markAttendance({
        ...mark,
        student_id: Number(mark.student_id),
        group_id: mark.group_id ? Number(mark.group_id) : null,
        status,
      });
      // The date stays: a teacher marking a day with several lessons on it is
      // about to mark the next one, and retyping the date every time is how
      // the wrong day gets recorded.
      setMark({ ...mark, lesson_time: '', group_id: '', comment: '' });
      students.reload();
    } catch (err) { setError(err.message); }
  };

  if (tab === 'schedule') {
    const slots = schedule.data?.slots || [];
    return (
      <Panel state={schedule} empty={!slots.length}>
        <table className="cab-table">
          <thead><tr><th>{t('cab_date')}</th><th>{t('cab_status')}</th><th>{t('cab_name')}</th></tr></thead>
          <tbody>
            {slots.map((s) => (
              <tr key={s.id}>
                <td data-label={t('cab_date')}>{weekdayName(lang, s.weekday)}, {s.time}</td>
                <td data-label={t('cab_status')}>{s.format}</td>
                <td data-label={t('cab_name')}>{s.student_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cab-note">{t('cab_no_reschedule')}</p>
      </Panel>
    );
  }

  if (tab === 'help') return <HelpTab />;
  if (tab === 'journal') return <TeacherJournal />;

  if (tab === 'lessons') {
    const myGroups = groups.data || [];
    const picked = myGroups.find((g) => String(g.id) === String(lessonGroup));
    const rows = lessons.data || [];
    return (
      <Panel state={groups} empty={!myGroups.length}>
        {error && <p className="cab-error">{error}</p>}
        <div className="cab-card">
          <h3>{t('cab_tab_lessons')}</h3>
          <p className="cab-muted">{t('cab_lessons_hint')}</p>
          <div className="cab-form cab-form-row">
            <label>
              {t('cab_tab_groups')}
              <Dropdown
                value={lessonGroup}
                onChange={(v) => { setLessonGroup(v); setEditingLesson(null); }}
                placeholder="—"
                options={myGroups.map((g) => ({ value: g.id, label: g.name }))}
              />
            </label>
          </div>
        </div>

        {picked && (
          <form className="cab-card cab-form" onSubmit={saveLesson}>
            <h3>{editingLesson ? t('cab_lesson_edit') : t('cab_lesson_add')}</h3>
            {/* Editing changes what the lesson was about, never when it was:
                moving a lesson to another date would silently detach the
                attendance already marked against it. */}
            {!editingLesson && (
              <div className="cab-form-row">
                <label>
                  {t('cab_date')}
                  <input type="date" required value={lesson.lesson_date}
                         onChange={(e) => setLesson({ ...lesson, lesson_date: e.target.value })} />
                </label>
                <label>
                  {t('cab_lesson_time')}
                  <input type="time" step={300} value={lesson.lesson_time}
                         onChange={(e) => setLesson({ ...lesson, lesson_time: e.target.value })} />
                </label>
              </div>
            )}
            <label>
              {t('cab_lesson_topic')}
              <input type="text" required maxLength={200} value={lesson.topic}
                     placeholder={t('cab_lesson_topic_hint')}
                     onChange={(e) => setLesson({ ...lesson, topic: e.target.value })} />
            </label>
            <label>
              {t('cab_lesson_summary')}
              <textarea rows={3} maxLength={5000} value={lesson.summary}
                        placeholder={t('cab_lesson_summary_hint')}
                        onChange={(e) => setLesson({ ...lesson, summary: e.target.value })} />
            </label>
            <div className="cab-form-row">
              <button type="submit">{t('cab_save')}</button>
              {editingLesson && (
                <button type="button" className="cab-btn-ghost" onClick={() => {
                  setEditingLesson(null);
                  setLesson({ lesson_date: '', lesson_time: '', topic: '', summary: '' });
                }}>{t('cab_cancel')}</button>
              )}
            </div>
          </form>
        )}

        {picked && (
          <Panel state={lessons} empty={!rows.length}>
            {rows.map((l) => (
              <div className="cab-card" key={l.id}>
                <div className="cab-lesson-head">
                  <div>
                    <h3>{l.topic}</h3>
                    <p className="cab-muted">{lessonMoment(l)}</p>
                  </div>
                  <div className="cab-form-row">
                    <button type="button" className="cab-btn-ghost" onClick={() => {
                      setEditingLesson(l.id);
                      setLesson({ lesson_date: asDate(l.lesson_date), lesson_time: l.lesson_time || '',
                                  topic: l.topic, summary: l.summary || '' });
                    }}>{t('cab_lesson_edit')}</button>
                    {/* Only an empty lesson can go, so the button is only
                        offered while nobody has been marked against it. */}
                    {!l.marked_students && (
                      <button type="button" className="cab-btn-ghost"
                              onClick={() => removeLesson(l.id)}>{t('cab_delete')}</button>
                    )}
                  </div>
                </div>
                {l.summary && <p>{l.summary}</p>}
                <div className="cab-lesson-marks">
                  <span className="cab-muted">{t('cab_tab_attendance')}</span>
                  {(picked.students || []).map((st) => (
                    <span className="cab-lesson-mark" key={st.id}>
                      {st.full_name}
                      <button type="button" onClick={() => markAtLesson(l.id, st.id, 'present')}>
                        {t('cab_present')}
                      </button>
                      <button type="button" onClick={() => markAtLesson(l.id, st.id, 'missed')}>
                        {t('cab_missed')}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </Panel>
        )}
      </Panel>
    );
  }

  if (tab === 'groups') {
    const rows = groups.data || [];
    return (
      <Panel state={groups} empty={!rows.length}>
        {rows.map((g) => (
          <div className="cab-card" key={g.id}>
            <h3>{g.name}</h3>
            <ul className="cab-list">
              {g.students.map((s) => <li key={s.id}>{s.full_name}</li>)}
            </ul>
          </div>
        ))}
      </Panel>
    );
  }

  if (tab === 'students') {
    const rows = students.data || [];
    // Whoever is selected in the attendance form, so their debt can be shown
    // next to the buttons that are about to be refused.
    const picked = rows.find((s) => String(s.id) === String(mark.student_id));
    const markBlocked = Boolean(picked?.debt?.blocked);
    // Only the courses this student actually attends with this teacher. An
    // empty choice is the right one for an individual lesson, which belongs to
    // no group at all.
    const theirGroups = (groups.data || []).filter((g) =>
      (g.students || []).some((st) => String(st.id) === String(mark.student_id))
    );
    // What the timetable says this student has that day. A lesson marked at a
    // time nobody teaches is usually a typo, and five identical rows on one
    // date are usually five presses of the same button.
    const scheduledTimes = mark.student_id && mark.lesson_date
      ? (schedule.data?.slots || [])
          .filter((slot) =>
            String(slot.student_id) === String(mark.student_id) &&
            slot.weekday === new Date(`${mark.lesson_date}T00:00:00Z`).getUTCDay())
          .map((slot) => slot.time)
      : [];
    return (
      <Panel state={students} empty={!rows.length}>
        {error && <p className="cab-error">{error}</p>}
        <div className="cab-card">
          <h3>{t('cab_tab_attendance')}</h3>
          <div className="cab-form cab-form-row">
            <label>
              {t('cab_tab_students')}
              <Dropdown
            value={mark.student_id}
            onChange={(v) => setMark({ ...mark, student_id: v })}
            placeholder={"—"}
            options={rows.map((s) => ({ value: s.id, label: s.full_name }))}
          />
            </label>
            <label>
              {t('cab_date')}
              <input type="date" value={mark.lesson_date}
                     onChange={(e) => setMark({ ...mark, lesson_date: e.target.value })} />
            </label>
            {/* Empty for a day with one lesson, which is most days. It is the
                time that tells one of several same-day lessons from another,
                so leaving it empty twice marks the same lesson twice. */}
            <label>
              {t('cab_lesson_time')}
              <input type="time" step={300} value={mark.lesson_time}
                     onChange={(e) => setMark({ ...mark, lesson_time: e.target.value })} />
            </label>
            <label>
              {t('cab_lesson_course')}
              <Dropdown
                value={mark.group_id}
                onChange={(v) => setMark({ ...mark, group_id: v })}
                options={[
                  // Spelled out rather than left blank: an individual lesson
                  // genuinely belongs to no course, and the dropdown offers no
                  // other way back out of a choice already made.
                  { value: '', label: t('cab_lesson_course_none') },
                  ...theirGroups.map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
            </label>
            <label>
              {t('cab_teacher_comment')}
              <input type="text" value={mark.comment}
                     onChange={(e) => setMark({ ...mark, comment: e.target.value })} />
            </label>
            <div className="cab-btn-row">
              <button type="button" disabled={!mark.student_id || !mark.lesson_date || markBlocked}
                      onClick={() => sendMark('present')}>{t('cab_mark_present')}</button>
              <button type="button" className="cab-btn-ghost"
                      disabled={!mark.student_id || !mark.lesson_date || markBlocked}
                      onClick={() => sendMark('missed')}>{t('cab_mark_missed')}</button>
            </div>
          </div>
          {/* The server refuses this anyway; saying so before the click means
              the teacher finds out before the student is sitting there. */}
          <DebtBanner debt={picked?.debt} />
          {scheduledTimes.length > 0 && (
            <p className="cab-note">
              {t('cab_scheduled_that_day', { times: scheduledTimes.join(', ') })}
            </p>
          )}
          <p className="cab-note">{t('cab_mark_note')}</p>
          <p className="cab-note">{t('cab_mark_same_day_note')}</p>
          <p className="cab-note">{t('cab_mark_debt_note')}</p>
        </div>
        <table className="cab-table">
          <thead>
            <tr><th>{t('cab_name')}</th><th>{t('cab_email')}</th><th>{t('cab_debt_title')}</th></tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className={s.debt?.blocked ? 'cab-row-blocked' : undefined}>
                <td data-label={t('cab_name')}>{s.full_name}</td>
                <td data-label={t('cab_email')}>{s.email}</td>
                <td data-label={t('cab_debt_title')}><DebtTag debt={s.debt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    );
  }

  if (tab === 'homework') {
    const rows = homework.data || [];
    return (
      <Panel state={homework}>
        {error && <p className="cab-error">{error}</p>}
        <form className="cab-form cab-form-row cab-card" onSubmit={createHw}>
          <label>
            {t('cab_name')}
            <input type="text" required value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label>
            {t('cab_due')}
            <input type="date" value={form.due_date}
                   onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </label>
          <label>
            {t('cab_tab_groups')}
            <Dropdown
            value={form.group_id}
            onChange={(v) => setForm({ ...form, group_id: v, student_id: '' })}
            placeholder={"—"}
            options={(groups.data || []).map((g) => ({ value: g.id, label: g.name }))}
          />
          </label>
          <label>
            {t('cab_tab_students')}
            <Dropdown
            value={form.student_id}
            onChange={(v) => setForm({ ...form, student_id: v, group_id: '' })}
            placeholder={"—"}
            options={(students.data || []).map((s) => ({ value: s.id, label: s.full_name }))}
          />
          </label>
          <label className="cab-wide">
            {t('cab_your_answer')}
            <textarea rows={3} value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </label>
          <button type="submit">{t('cab_add')}</button>
        </form>

        {rows.map((h) => (
          <div className="cab-card" key={h.id}>
            <h3>{h.title}</h3>
            <p className="cab-muted">
              {h.group_name || t('cab_tab_students')} · {t('cab_due')}: {asDate(h.due_date)}
            </p>
            {h.submissions.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
            {h.submissions.map((s) => (
              <GradeRow key={s.student_id} homeworkId={h.id} submission={s} onDone={homework.reload} />
            ))}
          </div>
        ))}
      </Panel>
    );
  }

  if (tab === 'finance') {
    const d = finance.data;
    return (
      <Panel state={finance} empty={!d}>
        {d && !d.configured && <p className="cab-muted">{t('cab_finance_none')}</p>}
        {d && (
          <>
            <table className="cab-table">
              <thead><tr><th>{t('cab_month')}</th><th>{t('cab_lessons')}</th><th>{t('cab_amount')}</th></tr></thead>
              <tbody>
                {d.months.map((m) => (
                  <tr key={m.month}>
                    <td data-label={t('cab_month')}>{asDate(m.month).slice(0, 7)}</td>
                    <td data-label={t('cab_lessons')}>{m.lessons}</td>
                    <td data-label={t('cab_amount')}>{d.configured ? money(m.net, d.currency) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {d.configured && <p className="cab-note">{t('cab_finance_net')} ({d.tax_percent}%)</p>}
          </>
        )}
      </Panel>
    );
  }

  return <PasswordTab />;
}

function GradeRow({ homeworkId, submission, onDone }) {
  const { t } = useLang();
  const [grade, setGrade] = useState(submission.grade || '');
  const [comment, setComment] = useState(submission.teacher_comment || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.gradeHomework(homeworkId, { student_id: submission.student_id, grade, comment });
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cab-sub">
      <p><strong>{submission.student_name}</strong> · {asDate(submission.submitted_at)}</p>
      <p className="cab-answer">{submission.text}</p>
      <div className="cab-form cab-form-row">
        <label>
          {t('cab_grade')}
          <input type="text" value={grade} onChange={(e) => setGrade(e.target.value)} />
        </label>
        <label className="cab-wide">
          {t('cab_teacher_comment')}
          <input type="text" value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        <button type="button" onClick={save} disabled={busy}>{t('cab_save')}</button>
      </div>
    </div>
  );
}

// ── Admin and owner ─────────────────────────────────────────────────────────
function StaffCabinet({ tab }) {
  const { t } = useLang();
  const { user } = useAuth();
  // '' = everyone, '1' = still here, '0' = gone. Kept in the query rather than
  // filtered on the client so the list stays short as the years pass.
  const [filter, setFilter] = useState('1');
  const users = useAsync(() => api.getStaffUsers(undefined, filter), [filter]);
  const log = useAsync(() => api.getActionLog(), []);
  const mailStatus = useAsync(() => api.getMailStatus(), []);
  const analytics = useAsync(() => (user.role === 'owner' ? api.getAnalytics() : Promise.resolve(null)), [user.role]);

  const [form, setForm] = useState({ email: '', phone: '', full_name: '', role: 'student', password: '' });
  const [error, setError] = useState('');
  // Which account is having its password replaced, and the new value. Kept
  // inline rather than in a browser prompt so it behaves like the rest of the
  // page and the field can be a real password input.
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [pwDone, setPwDone] = useState('');

  const resetPassword = (u) => { setPwFor(u); setNewPw(''); setPwDone(''); setError(''); };

  const savePassword = async () => {
    setError('');
    try {
      await api.setUserPassword(pwFor.id, newPw);
      setPwDone(`${t('cab_saved')}: ${pwFor.full_name || pwFor.email}`);
      setPwFor(null);
      setNewPw('');
    } catch (err) { setError(err.message); }
  };

  // What was just handed out, kept in memory only so the office can read it
  // back to the person. It is never sent anywhere and disappears on reload.
  const [justCreated, setJustCreated] = useState(null);

  const addUser = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.createStaffUser(form);
      setJustCreated({
        login: form.role === 'parent' ? form.phone : form.email,
        name: form.full_name,
        password: form.password,
      });
      setForm({ email: '', phone: '', full_name: '', role: 'student', password: '' });
      users.reload();
    } catch (err) { setError(err.message); }
  };

  const toggleActive = async (u) => {
    try {
      await api.updateStaffUser(u.id, { is_active: !u.is_active });
      users.reload();
    } catch (err) { setError(err.message); }
  };

  if (tab === 'people') {
    const rows = users.data || [];
    return (
      <Panel state={users}>
        {error && <p className="cab-error">{error}</p>}
        {pwDone && <p className="cab-note">{pwDone}</p>}
        <p className="cab-muted">{t('cab_password_note')}</p>
        <p className="cab-note">{t('cab_leaving_note')}</p>

        <nav className="cab-tabs">
          {[['1', 'cab_filter_active'], ['0', 'cab_filter_left'], ['', 'cab_filter_all']].map(([value, key]) => (
            <button key={key} type="button"
                    className={`cab-tab${filter === value ? ' active' : ''}`}
                    onClick={() => setFilter(value)}>
              {t(key)}
            </button>
          ))}
        </nav>

        {pwFor && (
          <div className="cab-form cab-form-row cab-card">
            <PasswordField label={`${t('cab_set_password')} — ${pwFor.full_name || pwFor.email}`}
                           value={newPw} onChange={setNewPw} />
            <button type="button" disabled={newPw.length < 8} onClick={savePassword}>{t('cab_save')}</button>
            <button type="button" className="cab-btn-ghost" onClick={() => setPwFor(null)}>{t('cab_delete')}</button>
          </div>
        )}

        <form className="cab-form cab-form-row cab-card" onSubmit={addUser}>
          {/* A parent signs in with their phone number; for everybody else the
              email is the login, and it has to be an address that exists —
              the server checks the domain before the account is made. */}
          <label>
            {t('cab_phone')}
            <input type="tel" required={form.role === 'parent'} value={form.phone}
                   placeholder="+998 90 123 45 67"
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </label>
          <label>
            {t('cab_email')}
            <input type="email" required={form.role !== 'parent'} value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label>
            {t('cab_name')}
            <input type="text" value={form.full_name}
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </label>
          <label>
            {t('cab_role')}
            <Dropdown
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              // Only an owner may mint staff, so an admin is not offered the
              // two roles the server would refuse anyway. The server checks too.
              options={[
                { value: 'student', label: 'student' },
                { value: 'parent', label: 'parent' },
                { value: 'teacher', label: 'teacher' },
                ...(user.role === 'owner'
                  ? [{ value: 'admin', label: 'admin' }, { value: 'owner', label: 'owner' }]
                  : []),
              ]}
            />
          </label>
          <PasswordField label={t('login_password')} value={form.password}
                         onChange={(v) => setForm({ ...form, password: v })} />
          <button type="submit" disabled={form.password.length < 8}>{t('cab_add')}</button>
          <p className="cab-note cab-wide">
            {form.role === 'parent' ? t('cab_parent_phone_note') : t('cab_email_real_note')}
          </p>
        </form>

        {justCreated && (
          <div className="cab-card">
            <p className="cab-note">{t('cab_new_account')}</p>
            <div className="cab-row">
              <span>
                <strong>{t('cab_account_for')}:</strong> {justCreated.name || justCreated.login}
                {' · '}{justCreated.login}
                {' · '}<code className="cab-answer cab-inline">{justCreated.password}</code>
              </span>
              <button type="button" className="cab-btn-ghost" onClick={() => setJustCreated(null)}>
                {t('cab_hide')}
              </button>
            </div>
          </div>
        )}

        {/* Sits under the account list because that is where the answer to
            "does this person have a login" belongs. */}
        <SiteTeachers
          onNeedAccount={(name) => setForm({ email: '', phone: '', full_name: name, role: 'teacher', password: '' })}
        />

        <table className="cab-table">
          <thead>
            <tr>
              <th>{t('cab_name')}</th><th>{t('cab_email')}</th><th>{t('cab_phone')}</th>
              <th>{t('cab_role')}</th><th>{t('cab_status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id}>
                <td data-label={t('cab_name')}>
                  {u.full_name || '—'}
                  {!u.is_active && <span className="cab-muted"> · {t('cab_left')}</span>}
                </td>
                <td data-label={t('cab_email')}>{u.email || '—'}</td>
                <td data-label={t('cab_phone')}>{u.phone || '—'}</td>
                <td data-label={t('cab_role')}>
                  {u.role}
                  {/* A parent whose children have all gone still has an account
                      that works. Say so, rather than leaving it to be noticed. */}
                  {u.role === 'parent' && u.is_active && (
                    <span className={u.active_children ? 'cab-muted' : 'cab-warn'}>
                      {' · '}
                      {u.active_children
                        ? `${u.active_children} ${t('cab_active_children')}`
                        : t('cab_no_active_children')}
                    </span>
                  )}
                </td>
                <td data-label={t('cab_status')}>
                  <div className="cab-btn-row">
                    <button type="button" className="cab-btn-ghost" onClick={() => toggleActive(u)}>
                      {u.is_active ? 'on' : 'off'}
                    </button>
                    {/* Password recovery cannot send email, so the office has
                        to be able to hand someone a new one on the spot. */}
                    <button type="button" className="cab-btn-ghost" onClick={() => resetPassword(u)}>
                      {t('cab_set_password')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    );
  }

  if (tab === 'help') return <HelpTab />;
  if (tab === 'applications') return <StaffApplications />;
  if (tab === 'catalogue') return <StaffCatalogue />;
  if (tab === 'students') return <StaffStudents />;
  if (tab === 'groups') return <StaffGroups />;

  if (tab === 'log') {
    const rows = log.data || [];
    return (
      <>
        <MailStatus state={mailStatus} />
        <Panel state={log} empty={!rows.length}>
          <table className="cab-table">
            <thead><tr><th>{t('cab_date')}</th><th>{t('cab_name')}</th><th>{t('cab_status')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td data-label={t('cab_date')}>{new Date(r.created_at).toLocaleString()}</td>
                  <td data-label={t('cab_name')}>{r.full_name || r.email || '—'} <span className="cab-muted">{r.role}</span></td>
                  <td data-label={t('cab_status')}>{r.action} {r.target && <span className="cab-muted">{r.target}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </>
    );
  }

  if (tab === 'analytics') {
    const d = analytics.data;
    return (
      <Panel state={analytics} empty={!d}>
        {d && (
          <>
            <div className="cab-stats">
              {Object.entries(d.users).map(([role, n]) => <Stat key={role} label={role} value={n} />)}
              <Stat label={t('cab_lessons_paid')} value={d.lessons.paid} />
              <Stat label={t('cab_lessons_used')} value={d.lessons.used} />
            </div>
            <table className="cab-table">
              <thead><tr><th>{t('cab_month')}</th><th>{t('cab_amount')}</th></tr></thead>
              <tbody>
                {d.revenue_by_month.map((m) => (
                  <tr key={`${m.month}-${m.currency}`}>
                    <td data-label={t('cab_month')}>{asDate(m.month).slice(0, 7)}</td>
                    <td data-label={t('cab_amount')}>{money(m.total, m.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Panel>
    );
  }

  return <PasswordTab />;
}

// ── Office: one student's whole enrolment ───────────────────────────────────
// Everything the front desk does day to day lives on this screen: contract,
// package, schedule, payments, attendance corrections, parents. Without it the
// only way to enrol somebody would be to call the API by hand.
function StaffStudents() {
  const { t, lang } = useLang();
  const students = useAsync(() => api.getStaffUsers('student'), []);
  const teachers = useAsync(() => api.getStaffUsers('teacher', '1'), []);
  const parents = useAsync(() => api.getStaffUsers('parent', '1'), []);
  const groups = useAsync(() => api.getStaffGroups(), []);
  const [picked, setPicked] = useState('');
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState('');
  const detail = useAsync(
    () => (picked ? api.getStaffStudent(picked) : Promise.resolve(null)),
    [picked]
  );

  // Forms are plain local state; each one clears itself once the server agrees.
  // Пустая строка, а не ноль: поле, которое не заполнили, означает «не
  // оговорено», и отправлять вместо этого ноль — значит записать в договор
  // цену ноль и скидку ноль, которых никто не обещал.
  const blankContract = {
    contract_start: '', term_months: '', price_per_month: '', discount_percent: '',
    lessons_per_week: '', lessons_per_month: '', note: '',
  };
  const [contract, setContract] = useState(blankContract);
  // id договора, который сейчас правят, и черновик его полей.
  const [editingContract, setEditingContract] = useState(null);
  const [contractDraft, setContractDraft] = useState(blankContract);
  const [pkg, setPkg] = useState({ lessons_paid: 12 });
  const [slot, setSlot] = useState({ weekday: '1', time: '', format: 'individual', teacher_id: '' });
  const [payment, setPayment] = useState({ amount: '', paid_at: '', note: '' });
  const [parentId, setParentId] = useState('');

  const run = async (fn, after) => {
    setError('');
    try {
      await fn();
      if (after) after();
      detail.reload();
    } catch (e) {
      setError(e.message);
    }
  };

  const d = detail.data;

  return (
    <>
      {error && <p className="cab-error">{error}</p>}

      <div className="cab-form cab-form-row cab-card">
        <label className="cab-wide">
          {t('cab_pick_student')}
          {/* The list is ordered still-here first, and anyone who has left is
              labelled — their card stays reachable for the records. */}
          <Dropdown
            value={picked}
            onChange={(v) => setPicked(v)}
            placeholder={"—"}
            options={(students.data || []).map((s) => ({ value: s.id, label: `${s.full_name || s.email}${s.is_active ? '' : ` · ${t('cab_left')}`}` }))}
          />
        </label>
      </div>

      {!picked && <p className="cab-muted">{t('cab_no_student_picked')}</p>}

      {picked && (
        <Panel state={detail} empty={!d}>
          {d && (
            <>
              <DebtBanner debt={d.debt} />
              <div className="cab-stats">
                <Stat label={t('cab_lessons_left')} value={d.package.lessons_left} />
                <Stat label={t('cab_lessons_used')} value={d.package.lessons_used} />
                <Stat label={t('cab_present')} value={d.attendance.present} />
                <Stat label={t('cab_missed')} value={d.attendance.missed} />
              </div>

              {/* Contract */}
              <div className="cab-card">
                <h3>{t('cab_contract')}</h3>
                <p className="cab-muted">{t('cab_contract_hint')}</p>
                {d.contracts.map((c) => (
                  editingContract === c.id ? (
                    <div className="cab-contract-edit" key={c.id}>
                      <ContractFields value={contractDraft} onChange={setContractDraft} />
                      <div className="cab-row">
                        <button type="button" onClick={() => run(
                          () => api.updateContract(c.id, contractTerms(contractDraft)),
                          () => setEditingContract(null)
                        )}>{t('cab_contract_save')}</button>
                        <button type="button" className="cab-btn-ghost"
                                onClick={() => setEditingContract(null)}>{t('cab_contract_cancel')}</button>
                      </div>
                    </div>
                  ) : (
                    <div className="cab-contract-row" key={c.id}>
                      <div className="cab-row">
                        <span>{asDate(c.contract_start)} — {asDate(c.contract_end)}</span>
                        <span className="cab-tag">{c.term_months} {t('cab_months_short')}</span>
                        <button type="button" className="cab-btn-ghost" onClick={() => {
                          setEditingContract(c.id);
                          setContractDraft({
                            contract_start: String(c.contract_start).slice(0, 10),
                            term_months: c.term_months ?? '',
                            // NUMERIC приходит из Postgres строкой «3000000.00» —
                            // в поле ввода это лишние нули, которых никто не писал.
                            price_per_month: c.price_per_month === null || c.price_per_month === undefined ? '' : Number(c.price_per_month),
                            discount_percent: Number(c.discount_percent ?? 0) || '',
                            lessons_per_week: c.lessons_per_week ?? '',
                            lessons_per_month: c.lessons_per_month ?? '',
                            note: c.note ?? '',
                          });
                        }}>{t('cab_contract_edit')}</button>
                        <button type="button" className="cab-btn-ghost"
                                onClick={() => run(() => api.deleteContract(c.id))}>{t('cab_delete')}</button>
                      </div>
                      <ContractTerms c={c} />
                    </div>
                  )
                ))}
                <div className="cab-form">
                  <ContractFields value={contract} onChange={setContract} />
                  <button type="button" disabled={!contract.contract_start}
                          onClick={() => run(
                            () => api.createContract({ student_id: Number(picked), ...contractTerms(contract) }),
                            () => setContract(blankContract)
                          )}>{t('cab_add')}</button>
                </div>
              </div>

              {/* Package */}
              <div className="cab-card">
                <h3>{t('cab_package')}</h3>
                <p className="cab-muted">{t('cab_package_hint')}</p>
                {d.packages.map((p) => (
                  <div className="cab-row" key={p.id}>
                    <span>{p.lessons_used} / {p.lessons_paid} · {asDate(p.purchased_at)}</span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.deletePackage(p.id))}>{t('cab_delete')}</button>
                  </div>
                ))}
                <div className="cab-form cab-form-row">
                  <label>
                    {t('cab_lessons_count')}
                    <input type="number" min={1} value={pkg.lessons_paid}
                           onChange={(e) => setPkg({ lessons_paid: e.target.value })} />
                  </label>
                  <button type="button"
                          onClick={() => run(
                            () => api.createPackage({
                              student_id: Number(picked),
                              lessons_paid: Number(pkg.lessons_paid),
                              contract_id: d.contracts[0]?.id ?? null,
                            }),
                            () => setPkg({ lessons_paid: 12 })
                          )}>{t('cab_add')}</button>
                </div>
              </div>

              {/* Schedule */}
              <div className="cab-card">
                <h3>{t('cab_tab_schedule')}</h3>
                <p className="cab-note">{t('cab_no_reschedule')}</p>
                {d.schedule.map((s) => (
                  <div className="cab-row" key={s.id}>
                    <span>{weekdayName(lang, s.weekday)}, {s.time} · {s.format} · {s.teacher_name || '—'}</span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.deleteSlot(s.id))}>{t('cab_delete')}</button>
                  </div>
                ))}
                <div className="cab-form cab-form-row">
                  <label>
                    {t('cab_weekday')}
                    <Dropdown
            value={slot.weekday}
            onChange={(v) => setSlot({ ...slot, weekday: v })}
            options={[1, 2, 3, 4, 5, 6, 0].map((n) => ({ value: n, label: weekdayName(lang, n) }))}
          />
                  </label>
                  <label>
                    {t('cab_time')}
                    <input type="time" value={slot.time}
                           onChange={(e) => setSlot({ ...slot, time: e.target.value })} />
                  </label>
                  <label>
                    {t('cab_format')}
                    <Dropdown
            value={slot.format}
            onChange={(v) => setSlot({ ...slot, format: v })}
            options={[{ value: "individual", label: t('cab_format_individual') }, { value: "group", label: t('cab_format_group') }]}
          />
                  </label>
                  <label>
                    {t('cab_teacher')}
                    <Dropdown
            value={slot.teacher_id}
            onChange={(v) => setSlot({ ...slot, teacher_id: v })}
            placeholder={"—"}
            options={(teachers.data || []).map((x) => ({ value: x.id, label: x.full_name || x.email }))}
          />
                  </label>
                  <button type="button" disabled={!slot.time}
                          onClick={() => run(
                            () => api.createSlot({
                              student_id: Number(picked),
                              teacher_id: slot.teacher_id ? Number(slot.teacher_id) : null,
                              weekday: Number(slot.weekday),
                              time: slot.time,
                              format: slot.format,
                            }),
                            () => setSlot({ ...slot, time: '' })
                          )}>{t('cab_add')}</button>
                </div>
              </div>

              {/* Payments */}
              <div className="cab-card">
                <h3>{t('cab_tab_payments')}</h3>
                {d.payments.map((p) => (
                  <div className="cab-row" key={p.id}>
                    <span>{asDate(p.paid_at)} · {money(p.amount, p.currency)} · {p.note || '—'}</span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.deletePayment(p.id))}>{t('cab_delete')}</button>
                  </div>
                ))}
                <div className="cab-form cab-form-row">
                  <label>
                    {t('cab_amount')}
                    <input type="number" min={1} value={payment.amount}
                           onChange={(e) => setPayment({ ...payment, amount: e.target.value })} />
                  </label>
                  <label>
                    {t('cab_date')}
                    <input type="date" value={payment.paid_at}
                           onChange={(e) => setPayment({ ...payment, paid_at: e.target.value })} />
                  </label>
                  <label className="cab-wide">
                    {t('cab_note')}
                    <input type="text" value={payment.note}
                           onChange={(e) => setPayment({ ...payment, note: e.target.value })} />
                  </label>
                  <button type="button" disabled={!payment.amount || !payment.paid_at}
                          onClick={() => run(
                            () => api.createPayment({
                              student_id: Number(picked),
                              amount: Number(payment.amount),
                              paid_at: payment.paid_at,
                              note: payment.note,
                            }),
                            () => setPayment({ amount: '', paid_at: '', note: '' })
                          )}>{t('cab_add')}</button>
                </div>
              </div>

              {/* Attendance corrections */}
              <div className="cab-card">
                <h3>{t('cab_tab_attendance')}</h3>
                <p className="cab-muted">{t('cab_mark_note')}</p>
                {d.attendance_records.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
                {d.attendance_records.map((r) => (
                  <div className="cab-row" key={r.id}>
                    <span className={r.status === 'present' ? 'cab-ok' : 'cab-warn'}>
                      {lessonMoment(r)}{r.group_name ? ` · ${r.group_name}` : ''} · {r.status === 'present' ? t('cab_present') : t('cab_missed')}
                      {r.teacher_name ? ` · ${r.teacher_name}` : ''}
                    </span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.deleteAttendance(r.id))}>{t('cab_delete')}</button>
                  </div>
                ))}
              </div>

              {/* Parents */}
              <div className="cab-card">
                <h3>{t('cab_parents')}</h3>
                {d.parents.map((p) => (
                  <div className="cab-row" key={p.id}>
                    <span>{p.full_name || p.email}</span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.unlinkParent(p.id, Number(picked)))}>
                      {t('cab_delete')}
                    </button>
                  </div>
                ))}
                <div className="cab-form cab-form-row">
                  <label className="cab-wide">
                    {t('cab_link_parent')}
                    <Dropdown
            value={parentId}
            onChange={(v) => setParentId(v)}
            placeholder={"—"}
            options={(parents.data || []).map((p) => ({ value: p.id, label: p.full_name || p.email }))}
          />
                  </label>
                  <button type="button" disabled={!parentId}
                          onClick={() => run(
                            () => api.linkParent({ parent_id: Number(parentId), student_id: Number(picked) }),
                            () => setParentId('')
                          )}>{t('cab_add')}</button>
                </div>
              </div>

              {/* The office writes remarks too — it is often the front desk
                  that hears from a parent, not the teacher. */}
              <Journal studentId={picked} canWrite />

              {/* Putting a student in a group is also what gives that group's
                  teacher access to them, so it belongs on this screen and not
                  only under Groups. */}
              <div className="cab-card">
                <h3>{t('cab_tab_groups')}</h3>
                {d.groups.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
                {d.groups.map((g) => (
                  <div className="cab-row" key={g.id}>
                    <span>{g.name}</span>
                    <button type="button" className="cab-btn-ghost"
                            onClick={() => run(() => api.removeGroupMember(g.id, Number(picked)))}>
                      {t('cab_delete')}
                    </button>
                  </div>
                ))}
                <div className="cab-form cab-form-row">
                  <label className="cab-wide">
                    {t('cab_tab_groups')}
                    <Dropdown
            value={groupId}
            onChange={(v) => setGroupId(v)}
            placeholder={"—"}
            options={(groups.data || [])
                        .filter((g) => !d.groups.some((own) => own.id === g.id)).map((g) => ({ value: g.id, label: `${g.name}${g.teacher_name ? ` · ${g.teacher_name}` : ''}` }))}
          />
                  </label>
                  <button type="button" disabled={!groupId}
                          onClick={() => run(
                            () => api.addGroupMember(Number(groupId), Number(picked)),
                            () => setGroupId('')
                          )}>{t('cab_add')}</button>
                </div>
              </div>
            </>
          )}
        </Panel>
      )}
    </>
  );
}

// ── The journal ─────────────────────────────────────────────────────────────
// One screen shared by the teacher, the parent and the office, because they are
// all asking the same question about the same person. The student is not shown
// it — the server refuses them, so this is not a matter of hiding a menu item.
function Journal({ studentId, canWrite }) {
  const { t } = useLang();
  const journal = useAsync(
    () => (studentId === undefined || studentId ? api.getJournal(studentId) : Promise.resolve(null)),
    [studentId]
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const d = journal.data;

  const write = async () => {
    setError('');
    try {
      await api.addNote(d.student.id, note);
      setNote('');
      journal.reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (id) => {
    setError('');
    try {
      await api.deleteNote(id);
      journal.reload();
    } catch (e) { setError(e.message); }
  };

  return (
    <Panel state={journal} empty={!d}>
      {d && (
        <>
          {error && <p className="cab-error">{error}</p>}
          <p className="cab-note">{t('cab_journal_hint')}</p>

          <div className="cab-stats">
            <Stat label={t('cab_present')} value={d.totals.present} />
            <Stat label={t('cab_missed')} value={d.totals.missed} />
            <Stat label={t('cab_hw_done')} value={d.totals.homework_done}
                  hint={`${t('cab_hw_set')}: ${d.totals.homework_set}`} />
            <Stat label={t('cab_hw_missing')} value={d.totals.homework_missing} />
          </div>

          <div className="cab-card">
            <h3>{t('cab_notes')}</h3>
            <p className="cab-muted">{t('cab_note_private')}</p>
            {d.notes.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
            {d.notes.map((n) => (
              <div className="cab-row" key={n.id}>
                <span>
                  {n.text}
                  <span className="cab-muted">
                    {' · '}{n.author_name || '—'} · {asDate(n.created_at)}
                  </span>
                </span>
                {canWrite && (
                  <button type="button" className="cab-btn-ghost" onClick={() => remove(n.id)}>
                    {t('cab_delete')}
                  </button>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="cab-form cab-form-row">
                <label className="cab-wide">
                  {t('cab_note_text')}
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                </label>
                <button type="button" disabled={!note.trim()} onClick={write}>{t('cab_add_note')}</button>
              </div>
            )}
          </div>

          <div className="cab-card">
            <h3>{t('cab_tab_attendance')}</h3>
            {d.attendance.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
            {d.attendance.map((a) => (
              <div className="cab-row" key={a.id}>
                <span>
                  <span className={a.status === 'present' ? 'cab-ok' : 'cab-warn'}>
                    {lessonMoment(a)}{a.group_name ? ` · ${a.group_name}` : ''} · {a.status === 'present' ? t('cab_present') : t('cab_missed')}
                  </span>
                  {a.comment && <span className="cab-muted"> · {a.comment}</span>}
                  {a.teacher_name && <span className="cab-muted"> · {a.teacher_name}</span>}
                </span>
              </div>
            ))}
          </div>

          <div className="cab-card">
            <h3>{t('cab_tab_homework')}</h3>
            {d.homework.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}
            {d.homework.map((h) => (
              <div className="cab-row" key={h.id}>
                <span>
                  {h.title}
                  <span className="cab-muted"> · {t('cab_due')}: {asDate(h.due_date)}</span>
                  {' · '}
                  <span className={h.submitted_at ? 'cab-ok' : 'cab-warn'}>
                    {h.submitted_at ? `${t('cab_hw_done')} ${asDate(h.submitted_at)}` : t('cab_hw_missing')}
                  </span>
                  {h.grade && <span> · {t('cab_grade')}: {h.grade}</span>}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

// The teacher picks whose journal to read; the parent's child is implied.
function TeacherJournal() {
  const { t } = useLang();
  const students = useAsync(() => api.getTeacherStudents(), []);
  const [picked, setPicked] = useState('');

  return (
    <>
      <div className="cab-form cab-form-row cab-card">
        <label className="cab-wide">
          {t('cab_pick_student')}
          <Dropdown
            value={picked}
            onChange={(v) => setPicked(v)}
            placeholder={"—"}
            options={(students.data || []).map((s) => ({ value: s.id, label: s.full_name || s.email }))}
          />
        </label>
      </div>
      {picked ? <Journal studentId={picked} canWrite />
              : <p className="cab-muted">{t('cab_journal_no_student')}</p>}
    </>
  );
}

// ── Office: the teachers the website already shows ──────────────────────────
// The site has had teacher profiles all along — photo, subject, biography —
// and they are catalogue entries, not accounts. Nobody could see from the
// cabinet which of the faces on the website can actually sign in and mark a
// register. This lists them side by side and links the two.
function SiteTeachers({ onNeedAccount }) {
  const { t } = useLang();
  const profiles = useAsync(() => api.getTeachers(), []);
  // Fetched here rather than taken from the People list, which is filtered to
  // whoever is still here — a profile linked to a departed teacher would
  // otherwise read as "no account".
  const accountList = useAsync(() => api.getStaffUsers('teacher'), []);
  const accounts = accountList.data;
  const [linking, setLinking] = useState({});
  const [error, setError] = useState('');

  const link = async (profile, userId) => {
    setError('');
    try {
      await api.updateCatalogueTeacher(profile.id, { user_id: Number(userId) });
      setLinking({ ...linking, [profile.id]: '' });
      profiles.reload();
    } catch (e) { setError(e.message); }
  };

  const accountFor = (profile) => (accounts || []).find((u) => u.id === profile.user_id);

  return (
    <div className="cab-card">
      <h3>{t('cab_site_teachers')}</h3>
      <p className="cab-muted">{t('cab_site_teachers_hint')}</p>
      {error && <p className="cab-error">{error}</p>}
      <Panel state={profiles} empty={!(profiles.data || []).length}>
        {(profiles.data || []).map((p) => {
          const account = accountFor(p);
          return (
            <div className="cab-row" key={p.id}>
              <span>
                <strong>{p.name}</strong>
                {p.subject && <span className="cab-muted"> · {p.subject}</span>}
                {' · '}
                {account
                  ? <span className="cab-ok">{t('cab_linked_to')} {account.email}</span>
                  : <span className="cab-warn">{t('cab_no_account')}</span>}
              </span>
              {!account && (
                <div className="cab-btn-row">
                  <Dropdown
            value={linking[p.id] || ''}
            onChange={(v) => setLinking({ ...linking, [p.id]: v })}
            placeholder={"—"}
            options={(accounts || []).filter((u) => u.role === 'teacher').map((u) => ({ value: u.id, label: u.full_name || u.email }))}
          />
                  <button type="button" className="cab-btn-ghost" disabled={!linking[p.id]}
                          onClick={() => link(p, linking[p.id])}>
                    {t('cab_link')}
                  </button>
                  <button type="button" className="cab-btn-ghost" onClick={() => onNeedAccount(p.name)}>
                    {t('cab_add')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </Panel>
    </div>
  );
}

// ── Office: applications ────────────────────────────────────────────────────
// The front desk's starting point. An application arrives, somebody rings the
// person, and the outcome is recorded here — including the one outcome that
// matters, which is turning them into a student without retyping their name.
// In the order they happen. The trial lesson comes after the call and before
// anybody is enrolled — it needs no account, which is the whole point of it.
const APP_STATUSES = ['new', 'contacted', 'trial', 'enrolled', 'declined'];

function StaffApplications() {
  const { t } = useLang();
  const applications = useAsync(() => api.getApplications(), []);
  const [filter, setFilter] = useState('new');
  const [enrolling, setEnrolling] = useState(null);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  // Which card is asking "are you sure". Deleting is one click away, but not
  // the click you already had your finger on: the status buttons sit in the
  // same row, and a mis-tap there would take the enquiry with it.
  const [confirming, setConfirming] = useState(null);

  const setStatus = async (application, status) => {
    setError('');
    try {
      await api.setApplicationStatus(application.id, status);
      applications.reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (application) => {
    setError('');
    try {
      await api.deleteApplication(application.id);
      setConfirming(null);
      applications.reload();
    } catch (e) { setError(e.message); }
  };

  // Creating the account and closing the application are one action, because
  // doing the first and forgetting the second is how a list stops being useful.
  const enrol = async () => {
    setError('');
    try {
      await api.createStaffUser({
        email: form.email,
        phone: enrolling.phone,
        password: form.password,
        role: 'student',
        full_name: enrolling.name,
      });
      await api.setApplicationStatus(enrolling.id, 'enrolled');
      setDone({ name: enrolling.name, email: form.email, password: form.password });
      setEnrolling(null);
      setForm({ email: '', password: '' });
      applications.reload();
    } catch (e) { setError(e.message); }
  };

  const rows = (applications.data || [])
    .filter((a) => (filter ? (a.status || 'new') === filter : true))
    .slice()
    .reverse();

  return (
    <Panel state={applications}>
      {error && <p className="cab-error">{error}</p>}
      <p className="cab-note">{t('cab_app_hint')}</p>

      {done && (
        <div className="cab-card">
          <p className="cab-note">{t('cab_app_created')}</p>
          <div className="cab-row">
            <span>
              <strong>{done.name}</strong> · {done.email} ·
              <code className="cab-answer cab-inline">{done.password}</code>
            </span>
            <button type="button" className="cab-btn-ghost" onClick={() => setDone(null)}>{t('cab_hide')}</button>
          </div>
        </div>
      )}

      <nav className="cab-tabs">
        {APP_STATUSES.map((s) => (
          <button key={s} type="button" className={`cab-tab${filter === s ? ' active' : ''}`}
                  onClick={() => setFilter(s)}>
            {t(`cab_app_${s}`)}
          </button>
        ))}
        <button type="button" className={`cab-tab${filter === '' ? ' active' : ''}`}
                onClick={() => setFilter('')}>{t('cab_filter_all')}</button>
      </nav>

      {rows.length === 0 && <p className="cab-muted">{t('cab_none')}</p>}

      {rows.map((a) => (
        <div className="cab-card" key={a.id}>
          <h3>{a.name} <span className="cab-muted">{asDate(a.date)}</span></h3>
          <p>
            <strong>{t('cab_app_phone')}:</strong> {a.phone}
            {a.telegram ? ` · ${a.telegram}` : ''}
            {a.age ? ` · ${a.age}` : ''}
          </p>
          {a.course && <p><strong>{t('cab_app_course')}:</strong> {a.course} {a.format ? `· ${a.format}` : ''}</p>}
          {(a.days || a.time) && <p><strong>{t('cab_app_when')}:</strong> {[a.days, a.time].filter(Boolean).join(' · ')}</p>}
          {a.purpose && <p><strong>{t('cab_app_wants')}:</strong> {a.purpose}</p>}
          {a.message && <p className="cab-answer">{a.message}</p>}
          {a.handled_by && (
            <p className="cab-muted">{t('cab_app_handled_by')}: {a.handled_by} · {asDate(a.handled_at)}</p>
          )}

          <div className="cab-btn-row">
            {APP_STATUSES.filter((s) => s !== 'enrolled').map((s) => (
              <button key={s} type="button"
                      className={(a.status || 'new') === s ? '' : 'cab-btn-ghost'}
                      onClick={() => setStatus(a, s)}>
                {t(`cab_app_${s}`)}
              </button>
            ))}
            {(a.status || 'new') !== 'enrolled' && (
              <button type="button" onClick={() => { setEnrolling(a); setForm({ email: '', password: '' }); }}>
                {t('cab_app_enrol')}
              </button>
            )}
            {confirming === a.id ? (
              <>
                <button type="button" className="cab-btn-danger" onClick={() => remove(a)}>
                  {t('cab_app_delete_confirm')}
                </button>
                <button type="button" className="cab-btn-ghost" onClick={() => setConfirming(null)}>
                  {t('cab_cancel')}
                </button>
              </>
            ) : (
              <button type="button" className="cab-btn-ghost" onClick={() => setConfirming(a.id)}>
                {t('cab_delete')}
              </button>
            )}
          </div>

          {enrolling?.id === a.id && (
            <div className="cab-form cab-form-row cab-sub">
              <label>
                {t('cab_name')}
                <input type="text" value={a.name} readOnly />
              </label>
              <label>
                {t('cab_email')}
                <input type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </label>
              <PasswordField label={t('login_password')} value={form.password}
                             onChange={(v) => setForm({ ...form, password: v })} />
              <button type="button" disabled={!form.email || form.password.length < 8} onClick={enrol}>
                {t('cab_app_enrol')}
              </button>
              <button type="button" className="cab-btn-ghost" onClick={() => setEnrolling(null)}>
                {t('cab_delete')}
              </button>
            </div>
          )}
        </div>
      ))}
    </Panel>
  );
}

// ── Office: groups and teacher pay ──────────────────────────────────────────
function StaffGroups() {
  const { t, lang } = useLang();
  const groups = useAsync(() => api.getStaffGroups(), []);
  const teachers = useAsync(() => api.getStaffUsers('teacher', '1'), []);
  const students = useAsync(() => api.getStaffUsers('student', '1'), []);
  const rates = useAsync(() => api.getTeacherRates(), []);
  const [form, setForm] = useState({ name: '', teacher_id: '' });
  const [member, setMember] = useState({});
  // Per-group draft of a lesson slot, keyed by group id.
  const [lesson, setLesson] = useState({});
  const [rate, setRate] = useState({ teacher_id: '', per_lesson: '', tax_percent: '' });
  const [error, setError] = useState('');

  const run = async (fn, after, reload) => {
    setError('');
    try {
      await fn();
      if (after) after();
      (reload || groups.reload)();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      {error && <p className="cab-error">{error}</p>}

      <div className="cab-form cab-form-row cab-card">
        <label>
          {t('cab_group_name')}
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          {t('cab_teacher')}
          <Dropdown
            value={form.teacher_id}
            onChange={(v) => setForm({ ...form, teacher_id: v })}
            placeholder={"—"}
            options={(teachers.data || []).map((x) => ({ value: x.id, label: x.full_name || x.email }))}
          />
        </label>
        <button type="button" disabled={!form.name}
                onClick={() => run(
                  () => api.createGroup({
                    name: form.name,
                    teacher_id: form.teacher_id ? Number(form.teacher_id) : null,
                  }),
                  () => setForm({ name: '', teacher_id: '' })
                )}>{t('cab_add')}</button>
      </div>

      <Panel state={groups}>
        {(groups.data || []).map((g) => (
          <div className="cab-card" key={g.id}>
            <div className="cab-row">
              <h3>{g.name} <span className="cab-muted">{g.teacher_name || '—'}</span></h3>
              <button type="button" className="cab-btn-ghost"
                      onClick={() => run(() => api.deleteGroup(g.id))}>{t('cab_delete')}</button>
            </div>
            {g.students.map((s) => (
              <div className="cab-row" key={s.id}>
                <span>{s.full_name}</span>
                <button type="button" className="cab-btn-ghost"
                        onClick={() => run(() => api.removeGroupMember(g.id, s.id))}>{t('cab_delete')}</button>
              </div>
            ))}
            <div className="cab-form cab-form-row">
              <label className="cab-wide">
                {t('cab_add_member')}
                <Dropdown
            value={member[g.id] || ''}
            onChange={(v) => setMember({ ...member, [g.id]: v })}
            placeholder={"—"}
            options={(students.data || []).map((s) => ({ value: s.id, label: s.full_name || s.email }))}
          />
              </label>
              <button type="button" disabled={!member[g.id]}
                      onClick={() => run(
                        () => api.addGroupMember(g.id, Number(member[g.id])),
                        () => setMember({ ...member, [g.id]: '' })
                      )}>{t('cab_add')}</button>
            </div>

            {/* One lesson for everyone in the group at once. Typing the same
                Tuesday six o'clock four times over is how a simple change
                turns into a job for somebody else. */}
            <div className="cab-form cab-form-row">
              <label>
                {t('cab_weekday')}
                <Dropdown
            value={(lesson[g.id] || {}).weekday ?? '1'}
            onChange={(v) => setLesson({ ...lesson, [g.id]: { ...lesson[g.id], weekday: v } })}
            options={[1, 2, 3, 4, 5, 6, 0].map((n) => ({ value: n, label: weekdayName(lang, n) }))}
          />
              </label>
              <label>
                {t('cab_time')}
                <input type="time" value={(lesson[g.id] || {}).time || ''}
                       onChange={(e) => setLesson({ ...lesson, [g.id]: { ...lesson[g.id], time: e.target.value } })} />
              </label>
              <button type="button" disabled={!(lesson[g.id] || {}).time || g.students.length === 0}
                      onClick={() => run(
                        () => api.scheduleGroup(g.id, {
                          weekday: Number((lesson[g.id] || {}).weekday ?? 1),
                          time: lesson[g.id].time,
                        }),
                        () => setLesson({ ...lesson, [g.id]: { ...lesson[g.id], time: '' } })
                      )}>{t('cab_schedule_group')}</button>
            </div>
          </div>
        ))}
      </Panel>

      <div className="cab-card">
        <h3>{t('cab_rates')}</h3>
        <p className="cab-muted">{t('cab_finance_net')}</p>
        {(rates.data || []).map((r) => (
          <div className="cab-row" key={r.teacher_id}>
            <span>{r.full_name} · {money(r.per_lesson, r.currency)} · {Number(r.tax_percent)}%</span>
          </div>
        ))}
        <div className="cab-form cab-form-row">
          <label>
            {t('cab_teacher')}
            <Dropdown
            value={rate.teacher_id}
            onChange={(v) => setRate({ ...rate, teacher_id: v })}
            placeholder={"—"}
            options={(teachers.data || []).map((x) => ({ value: x.id, label: x.full_name || x.email }))}
          />
          </label>
          <label>
            {t('cab_rate_per_lesson')}
            <input type="number" min={0} value={rate.per_lesson}
                   onChange={(e) => setRate({ ...rate, per_lesson: e.target.value })} />
          </label>
          <label>
            {t('cab_tax_percent')}
            <input type="number" min={0} max={100} value={rate.tax_percent}
                   onChange={(e) => setRate({ ...rate, tax_percent: e.target.value })} />
          </label>
          <button type="button" disabled={!rate.teacher_id || rate.per_lesson === '' || rate.tax_percent === ''}
                  onClick={() => run(
                    () => api.setTeacherRate({
                      teacher_id: Number(rate.teacher_id),
                      per_lesson: Number(rate.per_lesson),
                      tax_percent: Number(rate.tax_percent),
                    }),
                    () => setRate({ teacher_id: '', per_lesson: '', tax_percent: '' }),
                    rates.reload
                  )}>{t('cab_save')}</button>
        </div>
      </div>
    </>
  );
}

// ── Office: the public catalogue ────────────────────────────────────────────
// Courses, prices, teacher profiles, reviews and the results board — the whole
// of what a visitor sees. This used to be a separate page at /admin with a
// shared password of its own, which meant two ways in, two things to keep in
// step, and a password belonging to nobody editing the public site. It is a
// tab now: one sign-in, one list of who may edit, one activity log.
const CATALOGUE_SECTIONS = ['courses', 'teachers', 'prices', 'reviews', 'results'];

function StaffCatalogue() {
  const { t } = useLang();
  const [section, setSection] = useState('courses');
  return (
    <>
      <nav className="cab-tabs">
        {CATALOGUE_SECTIONS.map((name) => (
          <button key={name} type="button"
                  className={`cab-tab${section === name ? ' active' : ''}`}
                  onClick={() => setSection(name)}>
            {t(`cab_cat_${name}`)}
          </button>
        ))}
      </nav>
      <p className="cab-note">{t('cab_cat_live_note')}</p>
      {section === 'courses' && <CatalogueCourses />}
      {section === 'teachers' && <CatalogueTeachers />}
      {section === 'prices' && <CataloguePrices />}
      {section === 'reviews' && <CatalogueReviews />}
      {section === 'results' && <CatalogueResults />}
    </>
  );
}

// A programme or a list of certificates is typed one item per line, and blank
// lines are simply blank lines rather than empty bullet points on the site.
const asLines = (value) => (value || []).join('\n');
const fromLines = (text) => String(text || '').split('\n').map((l) => l.trim()).filter(Boolean);

// Delete asks first, in place. A browser confirm() would do the same job, but
// a modal dialog blocks the page it is asked from, and this cabinet is a page
// that keeps loading things.
function DeleteButton({ onDelete }) {
  const { t } = useLang();
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" className="cab-btn-ghost" onClick={() => setAsking(true)}>
        {t('cab_delete')}
      </button>
    );
  }
  return (
    <span className="cab-btn-row">
      <button type="button" className="cab-btn-ghost" onClick={() => { setAsking(false); onDelete(); }}>
        {t('cab_cat_confirm_delete')}
      </button>
      <button type="button" className="cab-btn-ghost" onClick={() => setAsking(false)}>
        {t('cab_cancel')}
      </button>
    </span>
  );
}

const COURSE_CATEGORIES = ['English', 'Math', 'Russian', 'Uzbek', 'German', 'Spanish'];
const EMPTY_COURSE = {
  category: 'English', icon: '📚', title: '', description: '', audience: '',
  program: [], formats: [], duration: '', levels: '', price_individual: '', note: '',
};

function CatalogueCourses() {
  const { t } = useLang();
  const courses = useAsync(() => api.getCourses(), []);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const edit = (course) => setDraft({
    ...course,
    programText: asLines(course.program),
    formatsText: asLines(course.formats),
  });

  const save = async () => {
    setError('');
    const body = {
      ...draft,
      program: fromLines(draft.programText),
      formats: fromLines(draft.formatsText),
    };
    delete body.programText;
    delete body.formatsText;
    try {
      if (draft.id) await api.updateCourse(draft.id, body);
      else await api.createCourse(body);
      setDraft(null);
      courses.reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (id) => {
    setError('');
    try { await api.deleteCourse(id); courses.reload(); } catch (e) { setError(e.message); }
  };

  return (
    <>
      {error && <p className="cab-error">{error}</p>}
      {!draft && (
        <div className="cab-btn-row">
          <button type="button" onClick={() => edit(EMPTY_COURSE)}>{t('cab_cat_add_course')}</button>
        </div>
      )}
      {draft && (
        <div className="cab-card">
          <h3>{draft.id ? t('cab_cat_edit_course') : t('cab_cat_add_course')}</h3>
          <div className="cab-form cab-form-row">
            <label>
              {t('cab_cat_title')}
              <input type="text" value={draft.title}
                     onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_category')}
              <Dropdown
                value={draft.category}
                onChange={(v) => setDraft({ ...draft, category: v })}
                options={COURSE_CATEGORIES.map((c) => ({ value: c, label: c }))}
              />
            </label>
            <label>
              {t('cab_cat_icon')}
              <input type="text" maxLength={4} value={draft.icon || ''}
                     onChange={(e) => setDraft({ ...draft, icon: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_duration')}
              <input type="text" value={draft.duration || ''}
                     onChange={(e) => setDraft({ ...draft, duration: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_levels')}
              <input type="text" value={draft.levels || ''}
                     onChange={(e) => setDraft({ ...draft, levels: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_price_individual')}
              <input type="text" value={draft.price_individual || ''}
                     onChange={(e) => setDraft({ ...draft, price_individual: e.target.value })} />
            </label>
          </div>
          <div className="cab-form">
            <label>
              {t('cab_cat_description')}
              <textarea rows={3} value={draft.description || ''}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_audience')}
              <input type="text" value={draft.audience || ''}
                     onChange={(e) => setDraft({ ...draft, audience: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_program')}
              <textarea rows={5} value={draft.programText}
                        onChange={(e) => setDraft({ ...draft, programText: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_formats')}
              <textarea rows={3} value={draft.formatsText}
                        onChange={(e) => setDraft({ ...draft, formatsText: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_note')}
              <input type="text" value={draft.note || ''}
                     onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
            </label>
          </div>
          <div className="cab-btn-row">
            <button type="button" disabled={!draft.title} onClick={save}>{t('cab_save')}</button>
            <button type="button" className="cab-btn-ghost" onClick={() => setDraft(null)}>{t('cab_cancel')}</button>
          </div>
        </div>
      )}
      <Panel state={courses} empty={!(courses.data || []).length}>
        {(courses.data || []).map((c) => (
          <div className="cab-row" key={c.id}>
            <span>
              {c.icon} <strong>{c.title}</strong>
              <span className="cab-muted"> · {c.category}{c.levels ? ` · ${c.levels}` : ''}</span>
            </span>
            <span className="cab-btn-row">
              <button type="button" className="cab-btn-ghost" onClick={() => edit(c)}>{t('cab_edit')}</button>
              <DeleteButton onDelete={() => remove(c.id)} />
            </span>
          </div>
        ))}
      </Panel>
    </>
  );
}

const EMPTY_TEACHER = {
  name: '', subject: '', experience: '', photo: '', education: '',
  age_min: '', age_max: '',
  short_bio: '', full_bio: '', certifications: [], achievements: [],
};

// The site is read in three languages, so the prose on a teacher's card is
// written in three. English is also the fallback for the other two, which is
// why it is the one that cannot be left empty.
const BIO_LANGS = [
  { suffix: '', label: 'English' },
  { suffix: '_ru', label: 'Русский' },
  { suffix: '_uz', label: 'O‘zbekcha' },
];

function CatalogueTeachers() {
  const { t } = useLang();
  const teachers = useAsync(() => api.getTeachers(), []);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const edit = (teacher) => setDraft({
    ...teacher,
    certificationsText: asLines(teacher.certifications),
    achievementsText: asLines(teacher.achievements),
  });

  const save = async () => {
    setError('');
    const body = {
      ...draft,
      certifications: fromLines(draft.certificationsText),
      achievements: fromLines(draft.achievementsText),
    };
    delete body.certificationsText;
    delete body.achievementsText;
    try {
      if (draft.id) await api.updateCatalogueTeacher(draft.id, body);
      else await api.createCatalogueTeacher(body);
      setDraft(null);
      teachers.reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (id) => {
    setError('');
    try { await api.deleteCatalogueTeacher(id); teachers.reload(); } catch (e) { setError(e.message); }
  };

  // A profile written in English only still shows on the Russian site, in
  // English. That is the right fallback and the wrong finished state, so the
  // list says which languages are still missing rather than letting it pass.
  const missingLangs = (teacher) => BIO_LANGS
    .filter((l) => l.suffix && !teacher[`short_bio${l.suffix}`])
    .map((l) => l.label);

  return (
    <>
      {error && <p className="cab-error">{error}</p>}
      {!draft && (
        <div className="cab-btn-row">
          <button type="button" onClick={() => edit(EMPTY_TEACHER)}>{t('cab_cat_add_teacher')}</button>
        </div>
      )}
      {draft && (
        <div className="cab-card">
          <h3>{draft.id ? t('cab_cat_edit_teacher') : t('cab_cat_add_teacher')}</h3>
          <div className="cab-form cab-form-row">
            <label>
              {t('cab_name')}
              <input type="text" value={draft.name}
                     onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_experience')}
              <input type="text" value={draft.experience || ''} placeholder="8 years"
                     onChange={(e) => setDraft({ ...draft, experience: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_age_min')}
              <input type="number" min="3" max="99" value={draft.age_min ?? ''} placeholder="15"
                     onChange={(e) => setDraft({ ...draft, age_min: e.target.value === '' ? '' : Number(e.target.value) })} />
            </label>
            <label>
              {t('cab_cat_age_max')}
              <input type="number" min="3" max="99" value={draft.age_max ?? ''} placeholder={t('cab_cat_age_max_hint')}
                     onChange={(e) => setDraft({ ...draft, age_max: e.target.value === '' ? null : Number(e.target.value) })} />
            </label>
            <label>
              {t('cab_cat_photo')}
              <input type="url" value={draft.photo || ''} placeholder="https://…"
                     onChange={(e) => setDraft({ ...draft, photo: e.target.value })} />
            </label>
          </div>
          {draft.photo && <img src={draft.photo} alt={draft.name} className="cab-photo-preview" />}
          {/* All three languages in one form rather than three passes over it:
              writing a biography again from memory is how the versions end up
              saying different things about the same person. */}
          {BIO_LANGS.map(({ suffix, label }) => (
            <div className="cab-form" key={label}>
              <h4 className="cab-subhead">{label}</h4>
              <label>
                {t('cab_cat_subject')}
                <input type="text" value={draft[`subject${suffix}`] || ''}
                       onChange={(e) => setDraft({ ...draft, [`subject${suffix}`]: e.target.value })} />
              </label>
              <label>
                {t('cab_cat_education')}
                <input type="text" value={draft[`education${suffix}`] || ''}
                       onChange={(e) => setDraft({ ...draft, [`education${suffix}`]: e.target.value })} />
              </label>
              <label>
                {t('cab_cat_short_bio')}
                <textarea rows={2} value={draft[`short_bio${suffix}`] || ''}
                          onChange={(e) => setDraft({ ...draft, [`short_bio${suffix}`]: e.target.value })} />
              </label>
              <label>
                {t('cab_cat_full_bio')}
                <textarea rows={4} value={draft[`full_bio${suffix}`] || ''}
                          onChange={(e) => setDraft({ ...draft, [`full_bio${suffix}`]: e.target.value })} />
              </label>
            </div>
          ))}
          <div className="cab-form">
            <label>
              {t('cab_cat_certifications')}
              <textarea rows={3} value={draft.certificationsText}
                        onChange={(e) => setDraft({ ...draft, certificationsText: e.target.value })} />
            </label>
            <label>
              {t('cab_cat_achievements')}
              <textarea rows={3} value={draft.achievementsText}
                        onChange={(e) => setDraft({ ...draft, achievementsText: e.target.value })} />
            </label>
          </div>
          <p className="cab-note">{t('cab_cat_bio_note')}</p>
          <div className="cab-btn-row">
            <button type="button" disabled={!draft.name || !draft.short_bio} onClick={save}>
              {t('cab_save')}
            </button>
            <button type="button" className="cab-btn-ghost" onClick={() => setDraft(null)}>{t('cab_cancel')}</button>
          </div>
        </div>
      )}
      <Panel state={teachers} empty={!(teachers.data || []).length}>
        {(teachers.data || []).map((p) => {
          const missing = missingLangs(p);
          return (
            <div className="cab-row" key={p.id}>
              <span>
                <strong>{p.name}</strong>
                {p.subject && <span className="cab-muted"> · {p.subject}</span>}
                {missing.length > 0 && (
                  <span className="cab-warn"> · {t('cab_cat_missing_langs', { langs: missing.join(', ') })}</span>
                )}
              </span>
              <span className="cab-btn-row">
                <button type="button" className="cab-btn-ghost" onClick={() => edit(p)}>{t('cab_edit')}</button>
                <DeleteButton onDelete={() => remove(p.id)} />
              </span>
            </div>
          );
        })}
      </Panel>
    </>
  );
}

function CataloguePrices() {
  const { t } = useLang();
  const prices = useAsync(() => api.getPrices(), []);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      await api.updatePrice(draft.id, draft);
      setDraft(null);
      prices.reload();
    } catch (e) { setError(e.message); }
  };

  return (
    <>
      {error && <p className="cab-error">{error}</p>}
      <Panel state={prices} empty={!(prices.data || []).length}>
        <table className="cab-table">
          <thead>
            <tr>
              <th>{t('cab_cat_course')}</th>
              <th>{t('cab_cat_group')}</th>
              <th>{t('cab_cat_mini_group')}</th>
              <th>{t('cab_cat_individual')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(prices.data || []).map((row) => {
              const editing = draft && draft.id === row.id;
              const cell = (field) => (editing
                ? (
                  <input type="text" value={draft[field] || ''}
                         onChange={(e) => setDraft({ ...draft, [field]: e.target.value || null })} />
                )
                : (row[field] || '—'));
              return (
                <tr key={row.id}>
                  <td data-label={t('cab_cat_course')}>{row.course}</td>
                  <td data-label={t('cab_cat_group')}>{cell('group')}</td>
                  <td data-label={t('cab_cat_mini_group')}>{cell('mini_group')}</td>
                  <td data-label={t('cab_cat_individual')}>{cell('individual')}</td>
                  <td>
                    {editing ? (
                      <span className="cab-btn-row">
                        <button type="button" onClick={save}>{t('cab_save')}</button>
                        <button type="button" className="cab-btn-ghost" onClick={() => setDraft(null)}>
                          {t('cab_cancel')}
                        </button>
                      </span>
                    ) : (
                      <button type="button" className="cab-btn-ghost" onClick={() => setDraft({ ...row })}>
                        {t('cab_edit')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Panel>
    </>
  );
}

function CatalogueReviews() {
  const { t } = useLang();
  const reviews = useAsync(() => api.getAllFeedbacks(), []);
  const [error, setError] = useState('');

  const run = async (fn) => {
    setError('');
    try { await fn(); reviews.reload(); } catch (e) { setError(e.message); }
  };

  return (
    <>
      {error && <p className="cab-error">{error}</p>}
      <p className="cab-note">{t('cab_cat_reviews_note')}</p>
      <Panel state={reviews} empty={!(reviews.data || []).length}>
        {[...(reviews.data || [])].reverse().map((fb) => (
          <div className="cab-card" key={fb.id}>
            <div className="cab-row">
              <span>
                <strong>{fb.name}</strong>
                <span className="cab-muted"> · {fb.course} · {'★'.repeat(fb.rating || 0)}</span>
              </span>
              <span className={fb.approved ? 'cab-ok' : 'cab-warn'}>
                {fb.approved ? t('cab_cat_published') : t('cab_cat_waiting')}
              </span>
            </div>
            <p>{fb.text}</p>
            <div className="cab-btn-row">
              {!fb.approved && (
                <button type="button" onClick={() => run(() => api.approveFeedback(fb.id))}>
                  {t('cab_cat_publish')}
                </button>
              )}
              <DeleteButton onDelete={() => run(() => api.deleteFeedback(fb.id))} />
            </div>
          </div>
        ))}
      </Panel>
    </>
  );
}

const EMPTY_RESULT = { name: '', course: '', result: '', date: '', certificate_url: '' };

function CatalogueResults() {
  const { t } = useLang();
  const results = useAsync(() => api.getResults(), []);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      if (draft.id) await api.updateResult(draft.id, draft);
      else await api.createResult(draft);
      setDraft(null);
      results.reload();
    } catch (e) { setError(e.message); }
  };

  const remove = async (id) => {
    setError('');
    try { await api.deleteResult(id); results.reload(); } catch (e) { setError(e.message); }
  };

  return (
    <>
      {error && <p className="cab-error">{error}</p>}
      <p className="cab-note">{t('cab_cat_results_note')}</p>
      {!draft && (
        <div className="cab-btn-row">
          <button type="button" onClick={() => setDraft({ ...EMPTY_RESULT })}>{t('cab_cat_add_result')}</button>
        </div>
      )}
      {draft && (
        <div className="cab-card cab-form cab-form-row">
          <label>
            {t('cab_name')}
            <input type="text" value={draft.name}
                   onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label>
            {t('cab_cat_course')}
            <input type="text" value={draft.course || ''}
                   onChange={(e) => setDraft({ ...draft, course: e.target.value })} />
          </label>
          <label>
            {t('cab_cat_result')}
            <input type="text" value={draft.result || ''} placeholder="IELTS 7.5"
                   onChange={(e) => setDraft({ ...draft, result: e.target.value })} />
          </label>
          <label>
            {t('cab_date')}
            <input type="text" value={draft.date || ''} placeholder="2026-06"
                   onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
          </label>
          <label>
            {t('cab_cat_certificate')}
            <input type="url" value={draft.certificate_url || ''} placeholder="https://…"
                   onChange={(e) => setDraft({ ...draft, certificate_url: e.target.value })} />
          </label>
          <div className="cab-btn-row">
            <button type="button" disabled={!draft.name || !draft.result} onClick={save}>{t('cab_save')}</button>
            <button type="button" className="cab-btn-ghost" onClick={() => setDraft(null)}>{t('cab_cancel')}</button>
          </div>
        </div>
      )}
      <Panel state={results} empty={!(results.data || []).length}>
        {(results.data || []).map((r) => (
          <div className="cab-row" key={r.id}>
            <span>
              <strong>{r.name}</strong>
              <span className="cab-muted"> · {r.course} · {r.result}{r.date ? ` · ${r.date}` : ''}</span>
            </span>
            <span className="cab-btn-row">
              <button type="button" className="cab-btn-ghost" onClick={() => setDraft({ ...r })}>{t('cab_edit')}</button>
              <DeleteButton onDelete={() => remove(r.id)} />
            </span>
          </div>
        ))}
      </Panel>
    </>
  );
}

// ── Shell ───────────────────────────────────────────────────────────────────
const TABS = {
  // The manual sits beside the password: both are about the person using the
  // cabinet rather than about the work, so they belong at the end together.
  student: ['overview', 'course', 'schedule', 'homework', 'attendance', 'payments', 'help', 'password'],
  parent: ['overview', 'course', 'journal', 'schedule', 'homework', 'attendance', 'payments', 'help', 'password'],
  teacher: ['schedule', 'lessons', 'groups', 'students', 'journal', 'homework', 'finance', 'help', 'password'],
  admin: ['applications', 'students', 'groups', 'people', 'catalogue', 'log', 'help', 'password'],
  owner: ['applications', 'students', 'groups', 'people', 'catalogue', 'log', 'analytics', 'help', 'password'],
};

export default function Cabinet() {
  const { t } = useLang();
  const { user, loading, signOut } = useAuth();
  const [tab, setTab] = useState(null);
  const [childId, setChildId] = useState(null);
  useSEO(t('nav_cabinet'), t('cab_login_subtitle'));

  const tabs = user ? TABS[user.role] || [] : [];
  const active = tab && tabs.includes(tab) ? tab : tabs[0];

  if (loading) return <div className="cab-page"><div className="container"><p className="cab-muted">{t('cab_loading')}</p></div></div>;
  if (!user) return <Navigate to="/login" replace />;

  const children = user.children || [];

  return (
    <div className="cab-page">
      <header className="cab-header">
        <HeaderArt />
        <div className="container cab-header-inner">
          <div>
            <h1>{t('cab_hello', { name: user.full_name || user.email })}</h1>
            <p>{user.role}</p>
          </div>
          <button className="logout-btn" type="button" onClick={signOut}>{t('cab_signout')}</button>
        </div>
      </header>

      <div className="container cab-body">
        {/* A parent signs in and their children are simply there — every one of
            them, named, with whether the lessons are paid for. The dropdown
            used to be the only way to find out a second child existed, and a
            parent with one child saw nothing at all. */}
        {user.role === 'parent' && (
          <div className="cab-childbar">
            <p className="cab-note">{t('cab_parent_readonly')}</p>
            {children.length === 0 && <p className="cab-muted">{t('cab_no_children')}</p>}
            {children.length > 0 && (
              <>
                <h2 className="cab-children-title">{t('cab_my_children')}</h2>
                <div className="cab-children">
                  {children.map((c) => {
                    const current = (childId || children[0].id) === c.id;
                    return (
                      <button key={c.id} type="button"
                              className={`cab-child${current ? ' active' : ''}${c.debt?.has_debt ? ' owing' : ''}`}
                              onClick={() => setChildId(c.id)}>
                        <span className="cab-child-name">{c.full_name || c.email}</span>
                        <span className="cab-child-debt"><DebtTag debt={c.debt} /></span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <nav className="cab-tabs">
          {tabs.map((name) => (
            <button key={name} type="button"
                    className={`cab-tab${name === active ? ' active' : ''}`}
                    onClick={() => setTab(name)}>
              {t(`cab_tab_${name}`)}
            </button>
          ))}
        </nav>

        {(user.role === 'student' || user.role === 'parent') && (
          <LearnerCabinet tab={active} childId={childId || children[0]?.id || null} />
        )}
        {user.role === 'teacher' && <TeacherCabinet tab={active} />}
        {(user.role === 'admin' || user.role === 'owner') && <StaffCabinet tab={active} />}
      </div>
    </div>
  );
}
